/**
 * The geometry behind the logo's hover flames, kept free of React so it can be unit-tested and
 * rendered outside the browser. The approach is the one in ste-vg's "SVG Fire" pen: a flame is a
 * closed path traced along a sine-wobbled centreline, redrawn every frame, rather than a sprite.
 *
 * Every random draw goes through an injected `random`, so a caller can make a frame reproducible.
 */

/** How many flames can be alive at once. Slots are recycled; the DOM tree never changes. */
export const POOL_SIZE = 44;

/** Samples per side of a flame's silhouette. Enough to read as a curve at logo scale. */
export const SAMPLES = 16;

export interface Flame {
  bornAt: number;
  /** Lifetime in ms; the flame is recycled once it elapses. */
  life: number;
  /** Root position, in element pixels from the left edge. */
  x: number;
  /** How far the root drifts sideways over the flame's life. */
  drift: number;
  /** Distance travelled upward over the flame's life. */
  rise: number;
  height: number;
  thickness: number;
  /**
   * Lick shape: how many wave cycles run over the flame's own length, how far the tip swings, how
   * fast the wave travels, and where in it this flame starts. Counting cycles over the flame
   * rather than per pixel is what keeps a tall flame from folding over itself.
   */
  cycles: number;
  amplitude: number;
  lickSpeed: number;
  phase: number;
  /** Embers are thin, fast, and fly the full height; the rest are body flames. */
  ember: boolean;
}

/**
 * The box the flames burn in, in px. Every flame dimension is a fraction of `height`, so the field
 * wants to be the space the fire should occupy — the sidebar's toolbar, not the logo's own tight
 * box, which leaves the fire nowhere to go but on top of the artwork.
 */
export interface Size {
  width: number;
  height: number;
}

export type Random = () => number;

const between = (random: Random, min: number, max: number) => min + random() * (max - min);

/**
 * A fresh flame, rooted on the bottom edge and biased toward the middle of the box.
 *
 * The reference pen gets slender flames for free on a 400x800 canvas, but this field is short and
 * wide: a flame scaled naively to it comes out either a stubby blob or — once narrowed — a
 * full-height stripe with its tip clipped off. Flames are therefore kept to the lower part of the
 * field so the whole silhouette sits in the clear space beneath the logo and only the taller licks
 * reach the artwork, with a few embers breaking past the top.
 */
export function spawnFlame(now: number, size: Size, random: Random = Math.random): Flame {
  const { width, height } = size;
  const ember = random() > 0.72;
  // Two draws averaged: a cheap bell curve, so the fire is densest at the centre.
  const bias = (random() + random()) / 2;
  // Cubed, so short licks are the common case and a tall flame is an occasional accent.
  const reach = Math.pow(random(), 1.7);
  // Small: a flame as tall as a 32px logo reads as a blob sitting on it, not as fire behind it.
  const flameHeight = ember ? between(random, 0.08, 0.14) * height : (0.16 + reach * 0.34) * height;
  return {
    bornAt: now,
    life: ember ? between(random, 380, 760) : between(random, 420, 900),
    x: width * (0.06 + bias * 0.88),
    drift: between(random, -1, 1) * width * 0.04,
    rise: (ember ? between(random, 0.55, 1.0) : between(random, 0.05, 0.16)) * height,
    height: flameHeight,
    thickness: (ember ? between(random, 0.12, 0.2) : between(random, 0.3, 0.42)) * flameHeight,
    cycles: between(random, 0.5, 1.1),
    // Scaled to the flame's own height, so a short lick curves as much as a tall one.
    amplitude: between(random, 0.18, 0.4) * flameHeight,
    lickSpeed: between(random, 4, 9),
    phase: random() * Math.PI * 2,
    ember,
  };
}

/**
 * Half-width of the silhouette at `u` (0 at the root, 1 at the tip).
 *
 * `u^0.35 * (1-u)^1.5` is a teardrop: it peaks at u = 0.35/1.85 ≈ 0.19 and tapers to nothing at
 * the tip, so the flame carries its bulk low and draws out to a long point. Dividing by the
 * function's own maximum makes `thickness` mean the widest width rather than an arbitrary scale
 * factor. A plain `(1-u)^n` taper was the first attempt and read as a blade of grass.
 */
const PROFILE_PEAK = Math.pow(0.19, 0.35) * Math.pow(0.81, 1.5);

export const halfWidth = (u: number, thickness: number) =>
  ((thickness / 2) * (Math.pow(u, 0.35) * Math.pow(1 - u, 1.5))) / PROFILE_PEAK;

/**
 * The wobbling centreline, in element pixels. The wave is a function of `u` rather than of absolute
 * y, so its cycle count is fixed per flame however far the flame has risen, and the sideways swing
 * is eased by `u^1.5` so the tip curls instead of whipping into a dart.
 */
export function centreline(flame: Flame, u: number, t: number) {
  const wave = Math.sin(u * flame.cycles * Math.PI * 2 + flame.phase + t * flame.lickSpeed);
  return flame.x + flame.drift * t + wave * flame.amplitude * Math.pow(u, 1.5);
}

/** Traces the silhouette at life fraction `t`: up the left edge to the tip, then back down. */
export function flamePath(flame: Flame, t: number, size: Size): string {
  // Rooted on the field's bottom edge, so the fire fills the space beneath the logo and climbs
  // toward it rather than starting on top of it.
  const baseY = size.height - flame.rise * t;
  // Flames shorten as they rise, the way the reference tweens its height down.
  const height = flame.height * (1 - 0.4 * t);
  const left: string[] = [];
  const right: string[] = [];

  for (let i = 0; i <= SAMPLES; i++) {
    const u = i / SAMPLES;
    const y = baseY - height * u;
    const cx = centreline(flame, u, t);
    const w = halfWidth(u, flame.thickness);
    left.push(`${(cx - w).toFixed(2)} ${y.toFixed(2)}`);
    right.push(`${(cx + w).toFixed(2)} ${y.toFixed(2)}`);
  }
  right.reverse();
  return `M${left.join('L')}L${right.join('L')}Z`;
}

/** Embers wink out; body flames hold their weight and drop away at the end. */
export const flameOpacity = (flame: Flame, t: number) => (flame.ember ? 0.9 : 0.7) * Math.min(1, 2.6 * (1 - t));

/** Milliseconds until the next flame should be lit. */
export const spawnDelay = (random: Random = Math.random) => between(random, 6, 24);
